/**
 * Silktide Cookie Banner (GDPR consent)
 * Dynamically loads the vendor CSS/JS and applies the banner config.
 */
import {
  isGDPRCountry,
  JAPANESE_LOCALE,
  LOCALE_OPTIONS
} from '@link/share/constants';
import { getGeoInfoByIP } from '@link/share/services/geo';
import { createCookieBannerConfig, type CookieBannerConfig } from './config';
// Import Silktide vendor files as URLs
import silktideCSS from './silktide-consent-manager.css?url';
import silktideJS from './silktide-consent-manager.js?url';

interface SilktideCookieBannerManager {
  initCookieBanner: () => void;
  updateCookieBannerConfig: (config: CookieBannerConfig) => void;
  injectScript: (url: string, loadOption?: 'async' | 'defer') => void;
}

declare global {
  interface Window {
    silktideCookieBannerManager?: SilktideCookieBannerManager;
  }
}

const LOCALES: readonly string[] = LOCALE_OPTIONS.map(locale => locale.value);

/**
 * Resolve the locale from the `/:lang` URL segment, since routing has not run
 * yet when the banner is initialized.
 */
const resolveLocale = (): string => {
  const [, segment] = window.location.pathname.split('/');
  if (LOCALES.includes(segment)) {
    return segment;
  }
  const stored = localStorage.getItem('locale');
  return stored && LOCALES.includes(stored) ? stored : JAPANESE_LOCALE;
};

const getPrivacyUrl = () => `${location.origin}/${resolveLocale()}/privacy`;

let loading: Promise<void> | null = null;

const loadScript = (src: string, onError: string) =>
  new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(onError));
    document.head.appendChild(script);
  });

/**
 * Load the Silktide Cookie Banner.
 * @returns Promise that resolves once the banner is configured
 */
export function loadSilktideCookieBanner(): Promise<void> {
  if (loading) {
    return loading;
  }

  const cssLink = document.createElement('link');
  cssLink.rel = 'stylesheet';
  cssLink.href = silktideCSS;
  document.head.appendChild(cssLink);

  loading = loadScript(silktideJS, 'Failed to load Silktide manager')
    .then(() => {
      window.silktideCookieBannerManager?.updateCookieBannerConfig(
        createCookieBannerConfig(getPrivacyUrl())
      );
    })
    .catch((error: unknown) => {
      // Drop the memoized promise so a transient failure can be retried.
      loading = null;
      throw error;
    });

  return loading;
}

/**
 * Initialize the Silktide Cookie Banner. Safe to call unconditionally:
 * the banner is only shown to visitors in GDPR countries.
 * Call this before React renders.
 */
export function initGDPRCookieBanner(): void {
  getGeoInfoByIP()
    .then(({ isEUCountry, country }) => {
      if (Boolean(isEUCountry) || isGDPRCountry(country)) {
        return loadSilktideCookieBanner();
      }
    })
    .catch(error => {
      console.error('Failed to load Silktide Cookie Banner:', error);
    });
}

export { createCookieBannerConfig, type CookieBannerConfig };
